import { z, ZodType } from 'zod/v3';

// Direct Zod algebra - operates directly on Zod schemas
export type ZodAlgebra<T> = {
  string: (schema: z.ZodString) => T;
  number: (schema: z.ZodNumber) => T;
  boolean: (schema: z.ZodBoolean) => T;
  any: (schema: z.ZodAny) => T;
  unknown: (schema: z.ZodUnknown) => T;
  void: (schema: z.ZodVoid) => T;
  null: (schema: z.ZodNull) => T;
  undefined: (schema: z.ZodUndefined) => T;
  never: (schema: z.ZodNever) => T;
  bigint: (schema: z.ZodBigInt) => T;
  symbol: (schema: z.ZodSymbol) => T;
  date: (schema: z.ZodDate) => T;
  literal: (schema: z.ZodLiteral<any>) => T;
  array: (schema: z.ZodArray<any>, element: T) => T;
  object: (
    schema: z.ZodObject<any>,
    properties: Record<string, T & { optional?: boolean }>
  ) => T;
  union: (schema: z.ZodUnion<any>, types: T[]) => T;
  intersection: (schema: z.ZodIntersection<any, any>, left: T, right: T) => T;
  tuple: (schema: z.ZodTuple<any>, elements: T[]) => T;
  record: (schema: z.ZodRecord<any, any>, key: T, value: T) => T;
  map: (schema: z.ZodMap<any, any>, key: T, value: T) => T;
  set: (schema: z.ZodSet<any>, element: T) => T;
  function: (schema: z.ZodFunction<any, any>, args: T[], returns: T) => T;
  lazy: (schema: z.ZodLazy<any>) => T;
  enum: (schema: z.ZodEnum<any>) => T;
  nativeEnum: (schema: z.ZodNativeEnum<any>) => T;
  promise: (schema: z.ZodPromise<any>, element: T) => T;
  optional: (schema: z.ZodOptional<any>, inner: T) => T;
  nullable: (schema: z.ZodNullable<any>, inner: T) => T;
  default: (schema: z.ZodDefault<any>, inner: T) => T;
  effects: (schema: z.ZodEffects<any, any, any>, inner: T) => T;
};

// Helper to access Zod's internal _def safely
const getZodDef = (schema: ZodType): any => (schema as any)._def;

// Helper to check if a schema has validation checks (like .min(), .max(), etc.)
const hasValidationChecks = (schema: ZodType): boolean => {
  const def = getZodDef(schema);
  return def.checks && Array.isArray(def.checks) && def.checks.length > 0;
};

// Direct fold over Zod schemas
const foldZodSchema = <T>(algebra: ZodAlgebra<T>) => {
  const fold = (schema: ZodType): T => {
    // Handle wrapper types first
    if (schema instanceof z.ZodOptional) {
      return algebra.optional(schema, fold(schema.unwrap()));
    }

    if (schema instanceof z.ZodNullable) {
      return algebra.nullable(schema, fold(schema.unwrap()));
    }

    if (schema instanceof z.ZodDefault) {
      return algebra.default(schema, fold(schema.removeDefault()));
    }

    if (schema instanceof z.ZodEffects) {
      return algebra.effects(schema, fold(getZodDef(schema).schema));
    }

    const def = getZodDef(schema);
    const typeName = def.typeName;

    // Check for validation constraints on primitive types
    if (hasValidationChecks(schema)) {
      // If the schema has validation checks, treat it as having effects
      return algebra.effects(
        schema as any,
        (() => {
          // Recursively fold the same schema but without considering the checks
          // to get the inner type behavior
          const schemaWithoutChecks = { ...schema };
          (schemaWithoutChecks as any)._def = { ...def, checks: [] };
          return fold(schemaWithoutChecks as ZodType);
        })()
      );
    }

    switch (typeName) {
      case 'ZodString':
        return algebra.string(schema as z.ZodString);

      case 'ZodNumber':
        return algebra.number(schema as z.ZodNumber);

      case 'ZodBoolean':
        return algebra.boolean(schema as z.ZodBoolean);

      case 'ZodAny':
        return algebra.any(schema as z.ZodAny);

      case 'ZodUnknown':
        return algebra.unknown(schema as z.ZodUnknown);

      case 'ZodVoid':
        return algebra.void(schema as z.ZodVoid);

      case 'ZodNull':
        return algebra.null(schema as z.ZodNull);

      case 'ZodUndefined':
        return algebra.undefined(schema as z.ZodUndefined);

      case 'ZodNever':
        return algebra.never(schema as z.ZodNever);

      case 'ZodBigInt':
        return algebra.bigint(schema as z.ZodBigInt);

      case 'ZodSymbol':
        return algebra.symbol(schema as z.ZodSymbol);

      case 'ZodDate':
        return algebra.date(schema as z.ZodDate);

      case 'ZodLiteral':
        return algebra.literal(schema as z.ZodLiteral<any>);

      case 'ZodArray':
        return algebra.array(schema as z.ZodArray<any>, fold(def.type));

      case 'ZodObject': {
        const shape = def.shape();
        const properties: Record<string, T & { optional?: boolean }> = {};

        for (const [key, fieldSchema] of Object.entries(shape)) {
          // Handle optional fields specially - fold their inner type without the optional wrapper
          if (fieldSchema instanceof z.ZodOptional) {
            const innerResult = fold(fieldSchema.unwrap());
            properties[key] = Object.assign(innerResult as any, {
              optional: true,
            });
          } else {
            const fieldResult = fold(fieldSchema as ZodType);
            properties[key] = fieldResult as T & { optional?: boolean };
          }
        }

        return algebra.object(schema as z.ZodObject<any>, properties);
      }

      case 'ZodUnion':
        return algebra.union(
          schema as z.ZodUnion<any>,
          def.options.map((option: ZodType) => fold(option))
        );

      case 'ZodIntersection':
        return algebra.intersection(
          schema as z.ZodIntersection<any, any>,
          fold(def.left),
          fold(def.right)
        );

      case 'ZodTuple':
        return algebra.tuple(
          schema as z.ZodTuple<any>,
          def.items.map((item: ZodType) => fold(item))
        );

      case 'ZodRecord': {
        const keyType = def.keyType || z.string();
        return algebra.record(
          schema as z.ZodRecord<any, any>,
          fold(keyType),
          fold(def.valueType)
        );
      }

      case 'ZodMap':
        return algebra.map(
          schema as z.ZodMap<any, any>,
          fold(def.keyType),
          fold(def.valueType)
        );

      case 'ZodSet':
        return algebra.set(schema as z.ZodSet<any>, fold(def.valueType));

      case 'ZodFunction': {
        // Extract function arguments - they're stored as a tuple schema
        const args = def.args
          ? (() => {
              const argsSchema = def.args;
              const argsDef = getZodDef(argsSchema);
              if (argsDef.typeName === 'ZodTuple') {
                // Map over the tuple items to get individual arg types
                return argsDef.items.map((item: ZodType) => fold(item));
              }
              return [];
            })()
          : [];

        return algebra.function(
          schema as z.ZodFunction<any, any>,
          args,
          def.returns ? fold(def.returns) : fold(z.unknown())
        );
      }

      case 'ZodLazy':
        return algebra.lazy(schema as z.ZodLazy<any>);

      case 'ZodEnum':
        return algebra.enum(schema as z.ZodEnum<any>);

      case 'ZodNativeEnum':
        return algebra.nativeEnum(schema as z.ZodNativeEnum<any>);

      case 'ZodPromise':
        return algebra.promise(schema as z.ZodPromise<any>, fold(def.type));

      default:
        // Fallback to unknown for unhandled types
        return algebra.unknown(z.unknown());
    }
  };

  return fold;
};

/**
 * Apply a schema algebra using the fold pattern
 * This is the primary way to transform schemas
 */
export const fold = <T>(algebra: ZodAlgebra<T>, schema: ZodType): T => {
  return foldZodSchema(algebra)(schema);
};
